import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  User, 
  Mail, 
  Phone, 
  Building, 
  Calendar,
  MessageSquare,
  Handshake,
  FileText,
  Edit,
  Save,
  X,
  ExternalLink,
  Clock,
  MapPin
} from 'lucide-react';
import apiService from '../services/api';
import EmailHistory from './EmailHistory';

const ContactProfile = ({ contact, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [editedContact, setEditedContact] = useState(contact);
  const [deals, setDeals] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadRelatedData();
  }, [contact.id]);

  const loadRelatedData = async () => {
    setLoading(true);
    try {
      // Load related deals
      const dealsData = await apiService.getDeals({ contact_id: contact.id });
      setDeals(dealsData);

      // Load company info if contact has a company
      if (contact.company_id) {
        const companyData = await apiService.getCRMCompany(contact.company_id);
        setCompany(companyData);
      }
    } catch (error) {
      console.error('Error loading related data:', error);
      setError('Failed to load related information');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await apiService.updateCRMContact(contact.id, editedContact);
      setIsEditing(false);
      if (onUpdate) onUpdate(editedContact);
    } catch (error) {
      console.error('Error updating contact:', error);
      setError('Failed to update contact');
    }
  };

  const handleCancel = () => {
    setEditedContact(contact);
    setIsEditing(false);
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'Prospect':
        return 'bg-yellow-100 text-yellow-800';
      case 'Member':
        return 'bg-green-100 text-green-800';
      case 'Former Member':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDealsStats = () => {
    const totalValue = deals.reduce((sum, deal) => sum + (deal.deal_value || 0), 0);
    const activeDeals = deals.filter(deal => !['close_lost', 'agreement_signed'].includes(deal.stage)).length;
    const wonDeals = deals.filter(deal => deal.stage === 'agreement_signed').length;
    
    return { totalValue, activeDeals, wonDeals };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const stats = getDealsStats();

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-start p-6 border-b border-gray-200">
        <div className="flex items-start space-x-4">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
            <User className="w-8 h-8 text-blue-600" />
          </div>
          
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h2 className="text-2xl font-bold text-gray-900">{contact.name}</h2>
              <Badge className={getStatusBadgeColor(contact.status)}>
                {contact.status}
              </Badge>
            </div>
            
            <p className="text-gray-600 mb-1">{contact.title || 'No Title'}</p>
            
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center">
                <Mail className="w-4 h-4 mr-1" />
                <a href={`mailto:${contact.email}`} className="text-blue-600 hover:text-blue-700">
                  {contact.email}
                </a>
              </div>
              
              {contact.phone && (
                <div className="flex items-center">
                  <Phone className="w-4 h-4 mr-1" />
                  <a href={`tel:${contact.phone}`} className="text-blue-600 hover:text-blue-700">
                    {contact.phone}
                  </a>
                </div>
              )}
              
              {company && (
                <div className="flex items-center">
                  <Building className="w-4 h-4 mr-1" />
                  <span>{company.name}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {isEditing ? (
            <>
              <Button onClick={handleSave} size="sm">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button onClick={handleCancel} variant="outline" size="sm">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </>
          ) : (
            <Button onClick={() => setIsEditing(true)} variant="outline" size="sm">
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
          
          <Button onClick={onClose} variant="outline" size="sm">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {error && (
        <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="emails">
              <MessageSquare className="w-4 h-4 mr-2" />
              Emails
            </TabsTrigger>
            <TabsTrigger value="deals">
              <Handshake className="w-4 h-4 mr-2" />
              Deals ({deals.length})
            </TabsTrigger>
            <TabsTrigger value="activity">
              <Clock className="w-4 h-4 mr-2" />
              Activity
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Contact Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isEditing ? (
                    <>
                      <div>
                        <Label htmlFor="edit-name">Name</Label>
                        <Input
                          id="edit-name"
                          value={editedContact.name}
                          onChange={(e) => setEditedContact(prev => ({ ...prev, name: e.target.value }))}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="edit-email">Email</Label>
                        <Input
                          id="edit-email"
                          type="email"
                          value={editedContact.email}
                          onChange={(e) => setEditedContact(prev => ({ ...prev, email: e.target.value }))}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="edit-phone">Phone</Label>
                        <Input
                          id="edit-phone"
                          value={editedContact.phone || ''}
                          onChange={(e) => setEditedContact(prev => ({ ...prev, phone: e.target.value }))}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="edit-title">Title</Label>
                        <Input
                          id="edit-title"
                          value={editedContact.title || ''}
                          onChange={(e) => setEditedContact(prev => ({ ...prev, title: e.target.value }))}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-medium">{contact.email}</p>
                      </div>
                      
                      {contact.phone && (
                        <div>
                          <p className="text-sm text-gray-600">Phone</p>
                          <p className="font-medium">{contact.phone}</p>
                        </div>
                      )}
                      
                      {contact.title && (
                        <div>
                          <p className="text-sm text-gray-600">Title</p>
                          <p className="font-medium">{contact.title}</p>
                        </div>
                      )}
                      
                      {contact.lead_source && (
                        <div>
                          <p className="text-sm text-gray-600">Lead Source</p>
                          <p className="font-medium">{contact.lead_source}</p>
                        </div>
                      )}
                      
                      <div>
                        <p className="text-sm text-gray-600">Created</p>
                        <p className="font-medium">{formatDate(contact.created_at)}</p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Company Information */}
              {company && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Building className="w-5 h-5 mr-2" />
                      Company Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Company</p>
                      <p className="font-medium">{company.name}</p>
                    </div>
                    
                    {company.industry && (
                      <div>
                        <p className="text-sm text-gray-600">Industry</p>
                        <p className="font-medium">{company.industry}</p>
                      </div>
                    )}
                    
                    {company.website && (
                      <div>
                        <p className="text-sm text-gray-600">Website</p>
                        <a 
                          href={company.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="font-medium text-blue-600 hover:text-blue-700 flex items-center"
                        >
                          {company.website}
                          <ExternalLink className="w-3 h-3 ml-1" />
                        </a>
                      </div>
                    )}
                    
                    {company.employee_count && (
                      <div>
                        <p className="text-sm text-gray-600">Employees</p>
                        <p className="font-medium">{company.employee_count}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Deal Statistics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Handshake className="w-5 h-5 mr-2" />
                    Deal Statistics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-blue-600">{stats.activeDeals}</p>
                      <p className="text-sm text-gray-600">Active Deals</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{stats.wonDeals}</p>
                      <p className="text-sm text-gray-600">Won Deals</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-purple-600">{formatCurrency(stats.totalValue)}</p>
                      <p className="text-sm text-gray-600">Total Value</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              {(contact.notes || isEditing) && (
                <Card>
                  <CardHeader>
                    <CardTitle>Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {isEditing ? (
                      <textarea
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows="4"
                        value={editedContact.notes || ''}
                        onChange={(e) => setEditedContact(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Add notes about this contact..."
                      />
                    ) : (
                      <p className="text-gray-700">{contact.notes || 'No notes available'}</p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="emails">
            <EmailHistory contactId={contact.id} />
          </TabsContent>

          <TabsContent value="deals" className="space-y-4">
            {deals.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Handshake className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No deals found for this contact</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {deals.map((deal) => (
                  <Card key={deal.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">{deal.deal_name}</h3>
                          <p className="text-sm text-gray-600 mb-2">{deal.company_name}</p>
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                            <span>{formatCurrency(deal.deal_value || 0)}</span>
                            <span>•</span>
                            <span>{deal.probability || 0}% probability</span>
                            {deal.term_months && (
                              <>
                                <span>•</span>
                                <span>{deal.term_months} months</span>
                              </>
                            )}
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <Badge variant="outline" className="mb-2">
                            {deal.stage?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}
                          </Badge>
                          
                          {deal.created_at && (
                            <p className="text-xs text-gray-500">
                              Created {formatDate(deal.created_at)}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="activity">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                    <div>
                      <p className="font-medium text-gray-900">Contact created</p>
                      <p className="text-sm text-gray-600">
                        Added to CRM on {formatDate(contact.created_at)}
                      </p>
                    </div>
                  </div>
                  
                  {contact.updated_at !== contact.created_at && (
                    <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-2 h-2 bg-gray-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-medium text-gray-900">Contact updated</p>
                        <p className="text-sm text-gray-600">
                          Last modified on {formatDate(contact.updated_at)}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {deals.length > 0 && (
                    <div className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-medium text-gray-900">Deal activity</p>
                        <p className="text-sm text-gray-600">
                          {deals.length} deal{deals.length !== 1 ? 's' : ''} associated with this contact
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ContactProfile;